/**
 * GMPanel Component - Game Master Adjustments Panel
 * 
 * This component provides Game Master tools for making real-time adjustments
 * during combat sessions, including HP modification, condition management,
 * environmental effects, and combat state control.
 * 
 * FEATURES:
 * - Token HP adjustment (damage, healing, temp HP)
 * - Condition application (frightened, poisoned, etc.)
 * - Initiative order modifications
 * - Environmental effect toggles
 * - Quick dice rolls for saves/checks
 * - Combat flow control (pause, skip turns, etc.)
 * 
 * D&D 5e 2024 INTEGRATION:
 * - Supports all standard conditions from PHB
 * - Proper damage/healing calculations
 * - Initiative adjustment mechanics
 * - Environmental hazard management
 * 
 * date_of_change: 2025-09-01 - Created for multiattack combat improvements
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Dices, HeartPulse, ShieldAlert } from 'lucide-react';
import './GMPanel.css';

const GMPanel = ({
  selectedToken,
  tokens = [],
  onTokenUpdate,
  onHitPointAdjustment,
  onDiceRoll,
  onCombatStateChange,
  combatStarted = false,
  diceSettings = {
    gmAutomaticRolls: true,
    playerAutomaticRolls: true
  },
  onDiceSettingsChange = () => {}
}) => {
  const [activeTab, setActiveTab] = useState('hp');
  const [hpAdjustment, setHpAdjustment] = useState('');
  const [tempHp, setTempHp] = useState('');
  const [hpAdjustmentPending, setHpAdjustmentPending] = useState(null);
  const [hpAdjustmentFeedback, setHpAdjustmentFeedback] = useState(null);
  const [selectedCondition, setSelectedCondition] = useState('');
  const [customDice, setCustomDice] = useState('1d20');
  const [diceModifier, setDiceModifier] = useState('0');
  const selectedTokenIdRef = useRef(selectedToken?.id);

  useEffect(() => {
    // Feedback and typed amounts describe one combatant only. Clear them when
    // initiative changes so a prior result is never presented under the next
    // creature's name. date_of_change: 2026-09-01
    selectedTokenIdRef.current = selectedToken?.id;
    setHpAdjustment('');
    setHpAdjustmentPending(null);
    setHpAdjustmentFeedback(null);
  }, [selectedToken?.id]);

  // @date_of_change: 2025-11-05 - Convert dice automation checkboxes into ON/OFF toggles routed through BattleRoom (rule_reference: AGENTS.md §dice_manual_hand-off)
  const handleDiceSettingToggle = useCallback((key) => {
    const nextValue = !diceSettings?.[key];
    if (typeof onDiceSettingsChange !== 'function') {
      console.error('[GMPanel] onDiceSettingsChange callback missing; dice toggle aborted.');
      return;
    }
    try {
      onDiceSettingsChange({ [key]: nextValue });
    } catch (err) {
      console.error(`[GMPanel] Failed to propagate dice toggle for ${key}:`, err);
      return;
    }
    if (onDiceRoll) {
      const labelMap = {
        gmAutomaticRolls: 'GM Automatic Rolls',
        playerAutomaticRolls: 'Player Automatic Rolls'
      };
      const label = labelMap[key] || key;
      const stateText = nextValue ? 'enabled' : 'disabled';
      onDiceRoll(`GM toggled ${label}: ${stateText}`, {
        type: 'gm-config',
        setting: key,
        enabled: nextValue
      });
    }
  }, [diceSettings, onDiceSettingsChange, onDiceRoll]);

  // D&D 5e 2024 Standard Conditions
  const conditions = [
    'Blinded', 'Charmed', 'Deafened', 'Frightened', 'Grappled',
    'Hidden', 'Incapacitated', 'Invisible', 'Paralyzed', 'Petrified', 'Poisoned',
    'Prone', 'Restrained', 'Stunned', 'Unconscious', 'Exhaustion'
  ];

  // Handle HP adjustment (damage or healing)
  const handleHPAdjustment = useCallback(async (type) => {
    if (!selectedToken || !hpAdjustment || hpAdjustmentPending) return;
    
    const adjustment = Number(hpAdjustment);
    if (!Number.isInteger(adjustment) || adjustment <= 0 || adjustment > 100000) {
      setHpAdjustmentFeedback({
        type: 'error',
        message: `${selectedToken.name} cannot be adjusted: amount must be an integer from 1 to 100000.`,
      });
      return;
    }

    if (typeof onHitPointAdjustment !== 'function') {
      setHpAdjustmentFeedback({
        type: 'error',
        message: `${selectedToken.name} cannot be adjusted because authoritative BattleRoom synchronization is unavailable.`,
      });
      return;
    }

    setHpAdjustmentPending(type);
    setHpAdjustmentFeedback(null);
    try {
      const result = await onHitPointAdjustment({
        tokenId: selectedToken.id,
        tokenName: selectedToken.name,
        type,
        amount: adjustment,
      });
      if (selectedTokenIdRef.current !== selectedToken.id) return;
      const operationLabel = type === 'heal' ? 'healing' : 'damage';
      const hpTransition = Number.isFinite(result?.hpBefore) && Number.isFinite(result?.hpAfter)
        ? ` HP ${result.hpBefore} → ${result.hpAfter}.`
        : '';
      const message = `${result?.applied ?? adjustment} ${operationLabel} applied.${hpTransition}`;
      setHpAdjustmentFeedback({ type: 'success', message });
      setHpAdjustment('');
      if (onDiceRoll) {
        onDiceRoll(
          type === 'heal'
            ? `GM heals ${selectedToken.name} for ${result?.applied ?? adjustment} HP`
            : `GM applies ${result?.applied ?? adjustment} damage to ${selectedToken.name}`,
          { total: result?.applied ?? adjustment, type }
        );
      }
    } catch (error) {
      if (selectedTokenIdRef.current !== selectedToken.id) return;
      setHpAdjustmentFeedback({
        type: 'error',
        message: error?.message || `The HP adjustment for ${selectedToken.name} was not committed.`,
      });
    } finally {
      if (selectedTokenIdRef.current === selectedToken.id) {
        setHpAdjustmentPending(null);
      }
    }
  }, [selectedToken, hpAdjustment, hpAdjustmentPending, onHitPointAdjustment, onDiceRoll]);

  // Handle temporary HP assignment
  const handleTempHP = useCallback(() => {
    if (!selectedToken || !tempHp) return;
    
    const tempHpValue = parseInt(tempHp);
    if (isNaN(tempHpValue) || tempHpValue < 0) return;

    const currentTempHp = selectedToken.hpObj?.temp || 0;
    const newTempHp = Math.max(currentTempHp, tempHpValue); // Temp HP doesn't stack, take higher value
    
    if (onTokenUpdate) {
      onTokenUpdate(selectedToken.id, {
        hpObj: {
          ...selectedToken.hpObj,
          temp: newTempHp
        },
        tempHp: newTempHp
      });
    }
    
    if (onDiceRoll) {
      onDiceRoll(`GM grants ${newTempHp} temporary HP to ${selectedToken.name}`, { total: newTempHp, type: 'tempHp' });
    }
    
    setTempHp('');
  }, [selectedToken, tempHp, onTokenUpdate, onDiceRoll]);

  // Handle condition application
  const handleCondition = useCallback((action) => {
    if (!selectedToken || !selectedCondition) return;
    
    const currentConditions = selectedToken.conditions || [];
    let newConditions = [...currentConditions];
    let message = '';
    
    if (action === 'add' && !currentConditions.includes(selectedCondition)) {
      newConditions.push(selectedCondition);
      message = `GM applies ${selectedCondition} to ${selectedToken.name}`;
    } else if (action === 'remove' && currentConditions.includes(selectedCondition)) {
      newConditions = newConditions.filter(c => c !== selectedCondition);
      message = `GM removes ${selectedCondition} from ${selectedToken.name}`;
    }
    
    if (onTokenUpdate) {
      onTokenUpdate(selectedToken.id, {
        conditions: newConditions
      });
    }
    
    if (onDiceRoll && message) {
      onDiceRoll(message, { condition: selectedCondition, action });
    }
  }, [selectedToken, selectedCondition, onTokenUpdate, onDiceRoll]);

  // Handle custom dice roll
  const handleCustomDiceRoll = useCallback(() => {
    if (!customDice) return;
    
    try {
      // Simple dice parser (1d20, 2d6+3, etc.)
      const diceMatch = customDice.match(/(\d+)d(\d+)/i);
      if (!diceMatch) return;
      
      const numDice = parseInt(diceMatch[1]);
      const diceSize = parseInt(diceMatch[2]);
      const modifier = parseInt(diceModifier) || 0;
      
      let total = 0;
      const rolls = [];
      
      for (let i = 0; i < numDice; i++) {
        const roll = Math.floor(Math.random() * diceSize) + 1;
        rolls.push(roll);
        total += roll;
      }
      
      total += modifier;
      
      const modText = modifier !== 0 ? (modifier > 0 ? `+${modifier}` : `${modifier}`) : '';
      const message = `GM rolls ${customDice}${modText}: [${rolls.join(', ')}]${modText} = ${total}`;
      
      if (onDiceRoll) {
        onDiceRoll(message, { 
          total, 
          rolls, 
          modifier, 
          formula: `${customDice}${modText}`,
          type: 'gm-roll'
        });
      }
    } catch (error) {
      console.error('[GMPanel] Error rolling dice:', error);
    }
  }, [customDice, diceModifier, onDiceRoll]);

  return (
    <div className="gm-panel">
      {/* Tab Navigation */}
      <div className="gm-tabs">
        <button 
          className={`gm-tab ${activeTab === 'hp' ? 'active' : ''}`}
          onClick={() => setActiveTab('hp')}
        >
          <HeartPulse size={14} aria-hidden="true" />
          <span>HP</span>
        </button>
        <button 
          className={`gm-tab ${activeTab === 'conditions' ? 'active' : ''}`}
          onClick={() => setActiveTab('conditions')}
        >
          <ShieldAlert size={14} aria-hidden="true" />
          <span>Conditions</span>
        </button>
        <button 
          className={`gm-tab ${activeTab === 'dice' ? 'active' : ''}`}
          onClick={() => setActiveTab('dice')}
        >
          <Dices size={14} aria-hidden="true" />
          <span>Dice</span>
        </button>
      </div>

      {/* Tab Content */}
      <div className="gm-content">
        {/* HP Management Tab */}
        {activeTab === 'hp' && (
          <div className="gm-hp-section">
            {selectedToken ? (
              <>
                <div className="gm-token-info">
                  <h4>{selectedToken.name}</h4>
                  <div className="hp-display">
                    <span className="hp-current">{selectedToken.hpObj?.current || selectedToken.hp || 0}</span>
                    <span className="hp-separator">/</span>
                    <span className="hp-max">{selectedToken.hpObj?.max || selectedToken.maxHp || 0}</span>
                    {(selectedToken.hpObj?.temp || selectedToken.tempHp) > 0 && (
                      <span className="hp-temp"> (+{selectedToken.hpObj?.temp || selectedToken.tempHp})</span>
                    )}
                  </div>
                </div>
                
                <div className="gm-hp-controls">
                  <div className="hp-adjustment">
                    <label>HP Adjustment:</label>
                    <input
                      type="number"
                      value={hpAdjustment}
                      onChange={(e) => setHpAdjustment(e.target.value)}
                      placeholder="Amount"
                      min="1"
                    />
                    <div className="hp-buttons">
                      <button 
                        className="damage-btn"
                        onClick={() => handleHPAdjustment('damage')}
                        disabled={!hpAdjustment || !!hpAdjustmentPending}
                      >
                        {hpAdjustmentPending === 'damage' ? 'Applying…' : 'Damage'}
                      </button>
                      <button 
                        className="heal-btn"
                        onClick={() => handleHPAdjustment('heal')}
                        disabled={!hpAdjustment || !!hpAdjustmentPending}
                      >
                        {hpAdjustmentPending === 'heal' ? 'Applying…' : 'Heal'}
                      </button>
                    </div>
                    {hpAdjustmentFeedback && (
                      <p
                        className={`gm-hp-feedback gm-hp-feedback--${hpAdjustmentFeedback.type}`}
                        role={hpAdjustmentFeedback.type === 'error' ? 'alert' : 'status'}
                      >
                        {hpAdjustmentFeedback.message}
                      </p>
                    )}
                  </div>
                  
                  <div className="temp-hp-section">
                    <label>Temporary HP:</label>
                    <input
                      type="number"
                      value={tempHp}
                      onChange={(e) => setTempHp(e.target.value)}
                      placeholder="Temp HP"
                      min="0"
                    />
                    <button 
                      className="temp-hp-btn"
                      onClick={handleTempHP}
                      disabled={!tempHp}
                    >
                      Grant
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="no-selection">
                <p>Select a token to manage HP</p>
              </div>
            )}
          </div>
        )}

        {/* Conditions Management Tab */}
        {activeTab === 'conditions' && (
          <div className="gm-conditions-section">
            {selectedToken ? (
              <>
                <div className="gm-token-info">
                  <h4>{selectedToken.name}</h4>
                  <div className="current-conditions">
                    <strong>Current Conditions:</strong>
                    {selectedToken.conditions && selectedToken.conditions.length > 0 ? (
                      <div className="condition-tags">
                        {selectedToken.conditions.map((condition, idx) => (
                          <span key={idx} className="condition-tag">
                            {condition}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="no-conditions">None</span>
                    )}
                  </div>
                </div>
                
                <div className="condition-controls">
                  <label>Condition:</label>
                  <select
                    value={selectedCondition}
                    onChange={(e) => setSelectedCondition(e.target.value)}
                  >
                    <option value="">Select condition...</option>
                    {conditions.map(condition => (
                      <option key={condition} value={condition}>
                        {condition}
                      </option>
                    ))}
                  </select>
                  
                  <div className="condition-buttons">
                    <button 
                      className="add-condition-btn"
                      onClick={() => handleCondition('add')}
                      disabled={!selectedCondition}
                    >
                      Apply
                    </button>
                    <button 
                      className="remove-condition-btn"  
                      onClick={() => handleCondition('remove')}
                      disabled={!selectedCondition}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="no-selection">
                <p>Select a token to manage conditions</p>
              </div>
            )}
          </div>
        )}

        {/* Dice Rolling Tab */}
        {activeTab === 'dice' && (
          <div className="gm-dice-section">
            <div className="dice-settings">
              <h5>Automatic Roll Modes</h5>
              <p className="dice-settings-detail">
                Manual dice inputs let the GM narrate results or use physical dice. Disable automatic rolls when you want to capture manual values.
              </p>
              <div className="dice-setting-row">
                <label htmlFor="gm-automatic-rolls-toggle">
                  GM Automatic Rolls
                </label>
                <button
                  id="gm-automatic-rolls-toggle"
                  type="button"
                  className={`dice-toggle ${diceSettings?.gmAutomaticRolls ? 'on' : 'off'}`}
                  aria-pressed={!!diceSettings?.gmAutomaticRolls}
                  aria-label={`Toggle GM automatic rolls ${diceSettings?.gmAutomaticRolls ? 'on' : 'off'}`}
                  onClick={() => handleDiceSettingToggle('gmAutomaticRolls')}
                >
                  {diceSettings?.gmAutomaticRolls ? 'ON' : 'OFF'}
                </button>
              </div>
              <div className="dice-setting-row">
                <label htmlFor="player-automatic-rolls-toggle">
                  Player Automatic Rolls
                </label>
                <button
                  id="player-automatic-rolls-toggle"
                  type="button"
                  className={`dice-toggle ${diceSettings?.playerAutomaticRolls ? 'on' : 'off'}`}
                  aria-pressed={!!diceSettings?.playerAutomaticRolls}
                  aria-label={`Toggle player automatic rolls ${diceSettings?.playerAutomaticRolls ? 'on' : 'off'}`}
                  onClick={() => handleDiceSettingToggle('playerAutomaticRolls')}
                >
                  {diceSettings?.playerAutomaticRolls ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>
            <div className="dice-controls">
              <div className="dice-input">
                <label>Dice Formula:</label>
                <input
                  type="text"
                  value={customDice}
                  onChange={(e) => setCustomDice(e.target.value)}
                  placeholder="1d20, 2d6, etc."
                />
              </div>
              
              <div className="modifier-input">
                <label>Modifier:</label>
                <input
                  type="number"
                  value={diceModifier}
                  onChange={(e) => setDiceModifier(e.target.value)}
                  placeholder="0"
                />
              </div>
              
              <button 
                className="roll-dice-btn"
                onClick={handleCustomDiceRoll}
                disabled={!customDice}
              >
                <Dices size={15} aria-hidden="true" />
                <span>Roll</span>
              </button>
            </div>
            
            <div className="quick-rolls">
              <h5>Quick Rolls:</h5>
              <div className="quick-roll-buttons">
                <button onClick={() => { setCustomDice('1d20'); setDiceModifier('0'); handleCustomDiceRoll(); }}>
                  d20
                </button>
                <button onClick={() => { setCustomDice('1d12'); setDiceModifier('0'); handleCustomDiceRoll(); }}>
                  d12
                </button>
                <button onClick={() => { setCustomDice('1d10'); setDiceModifier('0'); handleCustomDiceRoll(); }}>
                  d10
                </button>
                <button onClick={() => { setCustomDice('1d8'); setDiceModifier('0'); handleCustomDiceRoll(); }}>
                  d8
                </button>
                <button onClick={() => { setCustomDice('1d6'); setDiceModifier('0'); handleCustomDiceRoll(); }}>
                  d6
                </button>
                <button onClick={() => { setCustomDice('1d4'); setDiceModifier('0'); handleCustomDiceRoll(); }}>
                  d4
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

GMPanel.propTypes = {
  selectedToken: PropTypes.object,
  tokens: PropTypes.array,
  onTokenUpdate: PropTypes.func,
  onHitPointAdjustment: PropTypes.func,
  onDiceRoll: PropTypes.func,
  onCombatStateChange: PropTypes.func,
  combatStarted: PropTypes.bool
};

export default GMPanel;
